/**
 * Image Carousel Component
 * Handles Swiper.js initialization and configuration for image carousels
 */

class ImageCarousel {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.config = this.getConfigFromDataAttributes();

    // Wait for Swiper to be available
    this.waitForSwiper().then(() => {
      this.init();
    });
  }

  /**
   * Wait for Swiper library to be loaded
   */
  async waitForSwiper() {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait

    while (typeof Swiper === 'undefined' && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    if (typeof Swiper === 'undefined') {
      console.error('Swiper library not loaded');
      throw new Error('Swiper library not loaded');
    }
  }

  /**
   * Extract configuration from data attributes
   */
  getConfigFromDataAttributes() {
    const dataset = this.element.dataset;

    return {
      slidesDesktop: parseInt(dataset.slidesDesktop) || 4,
      slidesTablet: parseInt(dataset.slidesTablet) || 3,
      slidesMobile: parseInt(dataset.slidesMobile) || 2,
      autoplay: dataset.autoplay === 'true',
      autoplayDelay: parseInt(dataset.autoplayDelay) || 5000,
      loop: dataset.loop === 'true',
      carouselId: dataset.carouselId || 'default',
    };
  }

  /**
   * Initialize the Swiper carousel
   */
  init() {
    const swiperConfig = {
      // Responsive breakpoints
      breakpoints: {
        // Mobile
        0: {
          slidesPerView: this.config.slidesMobile,
          spaceBetween: 15,
        },
        // Tablet
        550: {
          slidesPerView: this.config.slidesTablet,
          spaceBetween: 20,
        },
        // Desktop
        750: {
          slidesPerView: this.config.slidesDesktop,
          spaceBetween: 25,
        },
      },

      // Enable loop if more slides than visible
      rewind:
        this.config.loop && this.getTotalSlides() > this.config.slidesDesktop,

      // Center slides when fewer slides than container
      centeredSlides: this.getTotalSlides() < this.config.slidesDesktop,

      // Touch and grab cursor
      grabCursor: true,

      // Keyboard navigation
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Mouse wheel navigation
      mousewheel: {
        forceToAxis: true,
      },

      // Navigation arrows
      navigation: {
        nextEl: `${this.getSelectorPrefix()} .swiper-button-next`,
        prevEl: `${this.getSelectorPrefix()} .swiper-button-prev`,
      },

      // Pagination
      pagination: {
        el: `${this.getSelectorPrefix()} .swiper-pagination`,
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 5,
      },

      // Autoplay configuration
      autoplay: this.config.autoplay
        ? {
            delay: this.config.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,

      // Accessibility
      a11y: {
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
        firstSlideMessage: 'This is the first image',
        lastSlideMessage: 'This is the last image',
        paginationBulletMessage: 'Go to image {{index}}',
      },

      // Performance
      watchSlidesProgress: true,
      watchSlidesVisibility: true,

      // Preload images
      preloadImages: false,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2,
      },

      // Events
      on: {
        init: () => {
          this.onSwiperInit();
        },
        slideChange: () => {
          this.onSlideChange();
        },
        reachBeginning: () => {
          this.onReachBeginning();
        },
        reachEnd: () => {
          this.onReachEnd();
        },
      },
    };

    // Initialize Swiper
    try {
      this.swiper = new Swiper(this.element, swiperConfig);
      this.addEventListeners();
    } catch (error) {
      console.error('Failed to initialize Swiper:', error);
    }
  }

  /**
   * Get CSS selector prefix for this specific carousel
   */
  getSelectorPrefix() {
    return `[data-carousel-id="${this.config.carouselId}"]`;
  }

  /**
   * Get total number of slides
   */
  getTotalSlides() {
    return this.element.querySelectorAll('.swiper-slide').length;
  }

  /**
   * Handle Swiper initialization
   */
  onSwiperInit() {
    // Add initialized class for CSS transitions
    this.element.classList.add('swiper-initialized');

    // Dispatch custom event
    this.element.dispatchEvent(
      new CustomEvent('carousel:initialized', {
        detail: { carousel: this, swiper: this.swiper },
      })
    );
  }

  /**
   * Handle slide change
   */
  onSlideChange() {
    const activeIndex = this.swiper.activeIndex;
    const totalSlides = this.swiper.slides.length;

    // Dispatch custom event
    this.element.dispatchEvent(
      new CustomEvent('carousel:slideChange', {
        detail: {
          activeIndex,
          totalSlides,
          carousel: this,
          swiper: this.swiper,
        },
      })
    );
  }

  /**
   * Handle reaching beginning
   */
  onReachBeginning() {
    this.element.dispatchEvent(
      new CustomEvent('carousel:reachBeginning', {
        detail: { carousel: this, swiper: this.swiper },
      })
    );
  }

  /**
   * Handle reaching end
   */
  onReachEnd() {
    this.element.dispatchEvent(
      new CustomEvent('carousel:reachEnd', {
        detail: { carousel: this, swiper: this.swiper },
      })
    );
  }

  /**
   * Add additional event listeners
   */
  addEventListeners() {
    // Pause autoplay on mouse enter (if autoplay is enabled)
    if (this.config.autoplay) {
      this.element.addEventListener('mouseenter', () => {
        if (this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });

      this.element.addEventListener('mouseleave', () => {
        if (this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    }

    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
        }
      }, 250);
    });

    // Handle visibility change (pause autoplay when tab is not visible)
    if (this.config.autoplay) {
      document.addEventListener('visibilitychange', () => {
        if (this.swiper && this.swiper.autoplay) {
          if (document.hidden) {
            this.swiper.autoplay.stop();
          } else {
            this.swiper.autoplay.start();
          }
        }
      });
    }
  }

  /**
   * Destroy the carousel
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Update the carousel (useful when content changes)
   */
  update() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  /**
   * Go to specific slide
   */
  slideTo(index, speed = 300) {
    if (this.swiper) {
      this.swiper.slideTo(index, speed);
    }
  }

  /**
   * Go to next slide
   */
  slideNext() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * Go to previous slide
   */
  slidePrev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }
}

/**
 * Initialize all image carousels on the page
 */
function initImageCarousels() {
  const carouselElements = document.querySelectorAll('.image-carousel-swiper');
  const carousels = [];

  carouselElements.forEach((element) => {
    try {
      const carousel = new ImageCarousel(element);
      carousels.push(carousel);
    } catch (error) {
      console.error('Failed to initialize image carousel:', error);
    }
  });

  return carousels;
}

/**
 * Auto-initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageCarousels);
} else {
  initImageCarousels();
}

/**
 * Re-initialize carousels when new content is loaded (e.g., AJAX)
 */
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const carouselElements = section.querySelectorAll('.image-carousel-swiper');

  carouselElements.forEach((element) => {
    if (!element.swiper) {
      new ImageCarousel(element);
    }
  });
});

/**
 * Clean up carousels when sections are unloaded
 */
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target;
  const carouselElements = section.querySelectorAll('.image-carousel-swiper');

  carouselElements.forEach((element) => {
    if (element.swiper) {
      element.swiper.destroy(true, true);
    }
  });
});

// Export for use in other scripts
window.ImageCarousel = ImageCarousel;
window.initImageCarousels = initImageCarousels;
